/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react';
import { Button, Modal, Popover, Spin, Tag, Select, Tooltip, message } from 'antd';
import s from './s.module.less';
import {
    EContactType,
    EServiceType,
    KlarityServiceTypeInfo,
    Meta,
    ServerMeta,
    TKlarityProgramItem,
    TKlarityServiceUiTableItem,
    TPatientGroupLimitation,
} from 'types/common';
import { getProviderPatientGroupAndLimitation, disableBySpecialty } from 'api/klarityService';
import {
    getOpsProviderPatientGroupAndLimitation,
    opsDisContinueProgram,
    updateAdminKlarityProgramState,
    getSpecialtiesConditionsByProviderId,
    opsDisableBySpecialty,
} from 'api/operation';
import CommonTable from 'components/CommonTable';
import { ColumnsType } from 'antd/es/table';
import { ESpecialty, NOT_SET, WEIGHT_LOSS_15_MINS_TEMPLATE } from 'constants/common';
import { getKlarityPriceAreaFromRecommendPrice, getKlarityPriceLevel } from 'utils/service';
import ProfileStore from 'store/Profile';
import { Email } from 'constants/contact';
import useKlarityProgram from 'hooks/useKlarityProgram';
import { updateKlarityProgramState, disContinueProgram } from 'api/common';
import { setConfigingProgramId } from 'utils/sessionStorage';
import { getSpecialtiesConditions } from 'api/applicationForm';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import emptyImage from './images/empty.png';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';

type IProps = {
    onClickEdit: (item: TKlarityServiceUiTableItem) => void;
    onClickAdd: () => void;
    data?: TKlarityServiceUiTableItem[];
    loading?: boolean;
    isOps?: boolean;
    // 运营端需要传当前操作的用户id
    providerId?: number;
    // showTypeCheckbox?: boolean;
    showEditButtons?: boolean;
    showSyncButton?: boolean;
    onClickSync?: () => void;
    licenseStates?: ServerMeta[];
    onClickConfigProgram?: (program: TKlarityProgramItem) => void;
    showProgramConfigNote?: () => void;
    isKiwiUser?: boolean;
    isServiceSetting?: boolean;
    hideHeader?: boolean;
    hideFooter?: boolean;
    emptyComponent?: React.ReactNode;
    refetchTableData?: () => void;
};

type TSpecialtyCondition = {
    conditions: string;
    providerId: number;
    specialtyId: number;
    specialtyName: string;
    id: number;
};

const ServiceTable = (props: IProps) => {
    const navigate = useNavigate();
    const {
        hideFooter,
        isServiceSetting,
        data = [],
        onClickAdd,
        onClickEdit,
        loading,
        isOps,
        providerId,
        showEditButtons,
        showSyncButton,
        onClickSync,
        licenseStates,
        onClickConfigProgram,
        showProgramConfigNote,
        isKiwiUser,
        hideHeader,
        emptyComponent,
        refetchTableData,
    } = props;
    const groupIdInfoMap = React.useRef<Record<string, TPatientGroupLimitation>>({});
    const [groupIdInfoMapFlag, setGroupIdInfoMapFlag] = React.useState({});
    const [fetchingGroupInfo, setFetchingGroupInfo] = React.useState(false);
    const displaySyncButton = showSyncButton && onClickSync && isOps;
    const [getProfileStore, setProfileStore] = ProfileStore.useStore();
    const [showDiscontinueModal, setShowDiscontinueModal] = React.useState(false);
    const [showEditStatesModal, setShowEditStatesModal] = React.useState(false);
    const [pendingProgram, setPendingProgram] = React.useState<TKlarityProgramItem>();
    const [pendingProgramStates, setPendingProgramStates] = React.useState<ServerMeta[]>([]);
    const [programData, programDataLoading, refetchProgram] = useKlarityProgram(isOps, providerId);
    const [updateProgramStateLoading, setUpdateProgramStateLoading] = React.useState(false);
    const [discontinueProgramLoading, setDiscontinueProgramLoading] = React.useState(false);
    const notSupportStateDicKeys = pendingProgram?.excludeState?.split(',') || [];
    const supportStates = licenseStates?.filter((item) => notSupportStateDicKeys.indexOf(item.dictKey) === -1) || [];

    const enrollPrograms = programData?.filter((item) => item.enrolled);
    const unEnrollPrograms = programData?.filter((item) => !item.enrolled);

    const hasEnrollProgram = enrollPrograms && enrollPrograms.length > 0;
    const hasUnEnrollPrograms = unEnrollPrograms && unEnrollPrograms.length > 0;
    //todo
    const programIsActiveInPPP = pendingProgram?.programStatus === 'A'; //If the Program is active on PPP, providers can discontinue this program with the pop up

    const [specialtiesConditions, setSpecialtiesConditions] = React.useState<TSpecialtyCondition[]>([]);
    const [fetchSpecialtiesConditionsLoading, setFetchSpecialtiesConditionsLoading] = React.useState(false);
    const [showRemoveSpeConfirmModal, setShowRemoveSpeConfirmModal] = React.useState(false);
    const [removeSpeLoading, setRemoveSpeLoading] = React.useState(false);
    const [pendingRemoveSpe, setPendingRemoveSpe] = React.useState<TKlarityServiceUiTableItem>();

    const [homeInfo] = useProviderHomeInfo();

    const canRemoveSpe =
        (!isOps && homeInfo?.ppaProvider && data && data.length > 1) || (isOps && data && data.length > 1);

    let showTableModule = hasEnrollProgram || hasUnEnrollPrograms;
    if (isOps || isKiwiUser) {
        showTableModule = hasEnrollProgram;
    }

    React.useEffect(() => {
        if (showEditStatesModal) {
            const defaultStateKeyArray = pendingProgram?.states?.split(',') || [];
            const defaultStateArray =
                licenseStates?.filter((item) => defaultStateKeyArray.indexOf(item.dictKey) !== -1) || [];
            setPendingProgramStates(defaultStateArray);
        }
    }, [showEditStatesModal, data, licenseStates]);

    const handleDiscontinue = async () => {
        setDiscontinueProgramLoading(true);
        let result: any;
        if (isOps && providerId) {
            result = await opsDisContinueProgram(providerId, pendingProgram?.id!);
        } else {
            result = await disContinueProgram(pendingProgram?.id!);
        }
        if (!result.error) {
            refetchProgram();
            setShowDiscontinueModal(false);
        }
        setDiscontinueProgramLoading(false);
    };

    const handleEditStates = async () => {
        setUpdateProgramStateLoading(true);
        const fn = isOps
            ? (programId: number, states: string) =>
                  providerId && updateAdminKlarityProgramState(programId, states, providerId)
            : updateKlarityProgramState;
        const result = await fn(pendingProgram?.id!, pendingProgramStates.map((item) => item.dictKey)?.join(','));
        if (!result.error) {
            refetchProgram();
            setShowEditStatesModal(false);
        }
        setUpdateProgramStateLoading(false);
    };

    const doFetchPatientGroupLimitation = async (id: number) => {
        setFetchingGroupInfo(true);
        let result: any;
        if (providerId && isOps) {
            result = await getOpsProviderPatientGroupAndLimitation(id);
        } else {
            result = await getProviderPatientGroupAndLimitation(id);
        }
        if (!result.error) {
            groupIdInfoMap.current[`${id}`] = result.data?.data;
            setGroupIdInfoMapFlag({});
        }
        setFetchingGroupInfo(false);
    };

    const fetchSpecialtiesConditions = async () => {
        setFetchSpecialtiesConditionsLoading(true);
        try {
            let result: any;
            if (isOps) {
                if (providerId) {
                    result = await getSpecialtiesConditionsByProviderId(providerId);
                }
            } else {
                result = await getSpecialtiesConditions();
            }
            if (result && !result.error) {
                setSpecialtiesConditions(result.data?.data || []);
            } else {
                message.error(result?.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetchSpecialtiesConditionsLoading(false);
        }
    };

    const onClickRemove = async (item: TKlarityServiceUiTableItem) => {
        setPendingRemoveSpe(item);
        setShowRemoveSpeConfirmModal(true);
    };

    const handleConfirmRemoveSpe = async () => {
        setRemoveSpeLoading(true);
        const fn = isOps ? opsDisableBySpecialty : disableBySpecialty;
        const result = await fn(pendingRemoveSpe?.serviceSpecialtyName!, providerId!);
        if (!result.error) {
            message.success('Removed successfully');
            setShowRemoveSpeConfirmModal(false);
            refetchTableData?.();
        }
        setRemoveSpeLoading(false);
    };

    useEffect(() => {
        fetchSpecialtiesConditions();
    }, [isServiceSetting]);

    const getColumnsByData = (
        tableData: KlarityServiceTypeInfo[],
        serviceSpecialtyName: string,
    ): ColumnsType<KlarityServiceTypeInfo> => {
        const inx: number[] = [];
        let state = '';
        for (let i = 0; i < tableData.length; i++) {
            const item = tableData[i];
            if (!state) {
                state = item.state?.dictKey || '';
            } else if (i === tableData.length - 1) {
                if (state !== item.state?.dictKey) {
                    inx.push(i);
                    inx.push(i + 1);
                } else {
                    inx.push(i + 1);
                }
            } else if (state !== item.state?.dictKey) {
                state = item.state?.dictKey || '';
                inx.push(i);
            }
        }
        //8% + 12% + 12% + 12% + 12% + 12% + 13% + 12% = 83%
        const serviceTypeColumns: ColumnsType<KlarityServiceTypeInfo> = [
            {
                title: <div className={s.tableTitle}>STATES</div>,
                dataIndex: 'state',
                key: 'state',
                width: '120px',
                render: (st: ServerMeta) => {
                    return <div className={s.colWrap}>{st?.content || ''}</div>;
                },
                onCell: (_, index) => {
                    for (let i = 0; i < inx.length; i++) {
                        const inxKey = inx[i];
                        if (index === 0) {
                            return {
                                rowSpan: inx[0] - index,
                            };
                        }
                        if (index === inxKey && i < inx.length - 1) {
                            return {
                                rowSpan: inx[i + 1] - index,
                            };
                        }
                    }
                    return {
                        rowSpan: tableData.length === 1 ? 1 : 0,
                    };
                },
            },
            {
                title: <div className={s.tableTitle}>SERVICE TYPE</div>,
                dataIndex: 'serviceType',
                key: 'serviceType',
                width: '160px',
                render: (val, reccord) => {
                    if (
                        serviceSpecialtyName === ESpecialty.WEIGHT_LOSS &&
                        val === EServiceType.INIT &&
                        reccord.klarityTemplate === WEIGHT_LOSS_15_MINS_TEMPLATE
                    ) {
                        return <div className={s.colWrap}>New patient initial visit 15mins</div>;
                    }
                    return <div className={s.colWrap}>{val}</div>;
                },
            },
            {
                title: <div className={s.tableTitle}>CUSTOMIZED NAME</div>,
                dataIndex: 'customizedServiceName',
                key: 'customizedServiceName',
                width: '120px',
                render: (val) => {
                    return <div className={s.colWrap}>{val || NOT_SET}</div>;
                },
            },
            {
                title: <div className={s.tableTitle}>PATIENT COST</div>,
                dataIndex: 'patientCost',
                key: 'patientCost',
                width: '140px',
                render: (val) => {
                    return <div className={s.colWrap}>${val}</div>;
                },
            },
            {
                title: <div className={s.tableTitle}>INTAKE FORM</div>,
                dataIndex: 'intakeFormFlag',
                key: 'intakeFormFlag',
                width: '140px',
                render: (val) => {
                    return <div className={s.colWrap}>{val ? 'Yes' : 'No'}</div>;
                },
            },
            {
                title: <div className={s.tableTitle}>PROVIDER SUPERBILL SUPPORT</div>,
                dataIndex: 'superBillSupport',
                key: 'superBillSupport',
                width: '180px',
                render: (val, row) => {
                    return val ? 'Yes' : 'No';
                },
            },
            {
                title: <div className={s.tableTitle}>INSURANCE CARRIER</div>,
                dataIndex: 'insurances',
                key: 'insurancesCarrier',
                width: '100px',
                render: (val, row) => {
                    const carriers = val ? Array.from(new Set(val.map((item) => item.carrier))) : [];
                    return (
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={<div className={s.tipText}>{carriers?.join(',')}</div>}
                        >
                            <div className={s.insurancesCell}>
                                <span>{carriers?.join(',')}</span>
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>INSURANCE PLAN</div>,
                dataIndex: 'insurances',
                key: 'insurancesPlan',
                width: '100px',
                render: (val, row) => {
                    const plans = val ? Array.from(new Set(val.map((item) => item.plan))) : [];
                    return (
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={<div className={s.tipText}>{plans?.join(',')}</div>}
                        >
                            <div className={s.insurancesCell}>{plans?.join(',')}</div>
                        </Tooltip>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>PRIOR AUTHORIZATION SUPPORT</div>,
                dataIndex: 'priorAuthorizationSupport',
                key: 'priorAuthorizationSupport',
                width: '180px',
                render: (val, row) => {
                    return val ? 'Yes' : 'No';
                },
            },
            {
                title: <div className={s.tableTitle}>MY EARNINGS</div>,
                dataIndex: 'earning',
                key: 'earning',
                width: '120px',
                render: (val) => {
                    return <div className={s.colWrap}>${val}</div>;
                },
            },
            // {
            //     title: (
            //         <div className={s.tableTitle}>
            //             PATIENT COST
            //         </div>
            //     ),
            //     dataIndex: 'patientCost',
            //     key: 'patientCost',
            //     width: '12%',
            //     render: (val) => {
            //         return (
            //             <div className={s.colWrap}>
            //                 ${val}
            //             </div>
            //         );
            //     },
            // },
            {
                title: <div className={s.tableTitle}>AVA PRICE INSIGHTS</div>,
                dataIndex: 'avaPriceInsight',
                key: 'avaPriceInsight',
                width: '120px',
                render: (val, item: KlarityServiceTypeInfo) => {
                    const priceArea = getKlarityPriceAreaFromRecommendPrice(val);
                    const [level, percent] = getKlarityPriceLevel(item.patientCost, priceArea);
                    return (
                        <div className={s.colWrap}>
                            {!level && (
                                <Tag
                                    color="#FFFBEB"
                                    style={{
                                        color: '#B45309',
                                    }}
                                >
                                    Average
                                </Tag>
                            )}
                            {level === 1 && (
                                <Tag
                                    color="#EFF6FF"
                                    style={{
                                        color: '#0369A1',
                                    }}
                                >
                                    Very competitive
                                </Tag>
                            )}
                            {level === 2 && (
                                <Tag
                                    color="#ECFDF5"
                                    style={{
                                        color: '#047857',
                                    }}
                                >
                                    Competitive
                                </Tag>
                            )}
                            {level === 3 && (
                                <Tag
                                    color="#FFFBEB"
                                    style={{
                                        color: '#B45309',
                                    }}
                                >
                                    Average
                                </Tag>
                            )}
                            {level === 4 && (
                                <Tag
                                    color="#FFF7ED"
                                    style={{
                                        color: '#C2410C',
                                    }}
                                >
                                    High
                                </Tag>
                            )}
                            {level === 5 && (
                                <Tag
                                    color="#FEF2F2"
                                    style={{
                                        color: '#B91C1C',
                                    }}
                                >
                                    Not competitive
                                </Tag>
                            )}
                        </div>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>CONTACT TYPE</div>,
                dataIndex: 'contactType',
                key: 'contactType',
                width: '120px',
                render: (val: EContactType[], item: KlarityServiceTypeInfo) => {
                    const isPrescription = item.serviceType === EServiceType.PRESCRIPTION_SERVICE;
                    return (
                        <div className={s.colWrap}>
                            {isPrescription && (
                                <span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="17"
                                        height="21"
                                        viewBox="0 0 17 21"
                                        fill="none"
                                    >
                                        <path
                                            d="M5.14118 10.5003H11.5698M5.14118 14.786H11.5698M13.7126 20.1431H2.99833C1.81486 20.1431 0.855469 19.1837 0.855469 18.0003V3.00028C0.855469 1.81681 1.81486 0.857422 2.99833 0.857422H8.9831C9.26726 0.857422 9.53978 0.970304 9.74071 1.17124L15.5417 6.97218C15.7426 7.17311 15.8555 7.44563 15.8555 7.72979V18.0003C15.8555 19.1837 14.8961 20.1431 13.7126 20.1431Z"
                                            stroke="#6B7280"
                                            strokeWidth="1.71429"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            )}
                            {val?.includes(EContactType.TELE_HEALTH) && (
                                <span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.31277 1.52801H7.64986V4.35866C7.64986 4.91872 7.86873 5.45584 8.25833 5.85187C8.64792 6.24789 9.17633 6.47037 9.7273 6.47037C10.2783 6.47037 10.8067 6.24789 11.1963 5.85187C11.5859 5.45584 11.8047 4.91872 11.8047 4.35866V1.52801H11.1418C10.9425 1.52801 10.7513 1.44752 10.6104 1.30424C10.4694 1.16096 10.3902 0.966634 10.3902 0.764007C10.3902 0.56138 10.4694 0.367052 10.6104 0.223773C10.7513 0.0804935 10.9425 1.96927e-07 11.1418 1.96927e-07H11.8483C12.0399 -0.00010016 12.2296 0.0381578 12.4067 0.11259C12.5837 0.187021 12.7446 0.29617 12.8801 0.433802C13.0157 0.571435 13.1232 0.734856 13.1966 0.914736C13.27 1.09462 13.3079 1.28743 13.308 1.48217V4.35866C13.3078 5.18753 13.0293 5.99151 12.5186 6.63765C12.0078 7.28378 11.2954 7.73337 10.4992 7.91205C10.5788 8.53447 10.8885 9.10276 11.3653 9.50152C11.8422 9.90029 12.4505 10.0996 13.0666 10.0591C13.6828 10.0185 14.2607 9.74112 14.683 9.28319C15.1053 8.82526 15.3403 8.22112 15.3403 7.59346V6.43064C14.916 6.2667 14.5607 5.95831 14.335 5.55798C14.1092 5.15766 14.027 4.69017 14.1023 4.23514C14.1776 3.7801 14.4057 3.36567 14.7479 3.06243C15.09 2.75919 15.525 2.58589 15.9787 2.57205C16.4324 2.55822 16.8769 2.7047 17.2363 2.98655C17.5957 3.26839 17.8478 3.66818 17.9498 4.11781C18.0518 4.56743 17.9973 5.03909 17.7956 5.45245C17.5939 5.86581 17.2574 6.1953 16.8435 6.3848V7.59346C16.8422 8.62487 16.4489 9.61588 15.7458 10.3595C15.0426 11.1031 14.084 11.5417 13.0702 11.5839C12.0564 11.626 11.0658 11.2683 10.3053 10.5854C9.54481 9.90264 9.07329 8.94755 8.98922 7.91969C8.18541 7.74772 7.46422 7.29999 6.94666 6.6516C6.42909 6.00322 6.14662 5.19364 6.14664 4.35866V1.48217C6.14664 1.28747 6.18438 1.09467 6.25771 0.914794C6.33103 0.734918 6.4385 0.571491 6.57398 0.433848C6.70946 0.296206 6.87029 0.187046 7.04728 0.112605C7.22428 0.0381633 7.41397 -0.000100167 7.60551 1.96927e-07H8.31277C8.41148 1.95432e-07 8.50921 0.0197618 8.6004 0.0581567C8.69159 0.0965517 8.77445 0.152828 8.84424 0.223773C8.91403 0.294717 8.9694 0.378941 9.00717 0.471634C9.04494 0.564328 9.06438 0.663676 9.06438 0.764007C9.06438 0.864338 9.04494 0.963686 9.00717 1.05638C8.9694 1.14907 8.91403 1.2333 8.84424 1.30424C8.77445 1.37519 8.69159 1.43146 8.6004 1.46986C8.50921 1.50825 8.41148 1.52801 8.31277 1.52801ZM1.38296 1.70679H5.01923V3.2348H1.50246V13.2624H16.4196V11.2997L16.4888 11.2309C17.2679 10.4387 17.7714 9.40971 17.9228 8.30017V13.3846C17.9228 13.5692 17.8871 13.752 17.8176 13.9226C17.7481 14.0932 17.6462 14.2481 17.5178 14.3787C17.3893 14.5092 17.2369 14.6128 17.0691 14.6834C16.9013 14.754 16.7215 14.7904 16.5399 14.7904H10.3271L10.9051 16.472H12.7503C12.9496 16.472 13.1408 16.5525 13.2817 16.6958C13.4227 16.839 13.5019 17.0334 13.5019 17.236C13.5019 17.4386 13.4227 17.6329 13.2817 17.7762C13.1408 17.9195 12.9496 18 12.7503 18H5.17181C5.07311 18 4.97537 17.9802 4.88418 17.9418C4.79299 17.9034 4.71013 17.8472 4.64034 17.7762C4.57055 17.7053 4.51518 17.6211 4.47741 17.5284C4.43964 17.4357 4.4202 17.3363 4.4202 17.236C4.4202 17.1357 4.43964 17.0363 4.47741 16.9436C4.51518 16.8509 4.57055 16.7667 4.64034 16.6958C4.71013 16.6248 4.79299 16.5685 4.88418 16.5301C4.97537 16.4917 5.07311 16.472 5.17181 16.472H7.017L7.59574 14.7904H1.38296C1.20134 14.7904 1.02151 14.754 0.853722 14.6834C0.685934 14.6128 0.533478 14.5092 0.405059 14.3787C0.145704 14.115 0 13.7575 0 13.3846V3.11256C0 2.73973 0.145704 2.38217 0.405059 2.11853C0.664413 1.8549 1.01617 1.70679 1.38296 1.70679Z"
                                            fill="#6B7280"
                                        />
                                    </svg>
                                </span>
                            )}
                            {val?.includes(EContactType.IN_PERSON) && (
                                <span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21L21 21M19 21H14M5 21L3 21M5 21H10M9 6.99998H10M9 11H10M14 6.99998H15M14 11H15M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21M10 21H14"
                                            stroke="#6B7280"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>PATIENT ACCEPTED</div>,
                width: '140px',
                render: (val, item: KlarityServiceTypeInfo) => {
                    const isAllAccept = item.allPatientGroup || !item.qtyPatientGroup || item.qtyPatientGroup === 0;
                    const key: string = `${item.groupAndLimitationId}`;
                    const tagetData: undefined | TPatientGroupLimitation = groupIdInfoMap.current[key];
                    const isPrescription = item.serviceType === EServiceType.PRESCRIPTION_SERVICE;
                    if (isPrescription) {
                        return <div className={s.colWrap}>-</div>;
                    }
                    return (
                        <div className={s.colWrap}>
                            {isAllAccept && <span>All patients</span>}
                            {!isAllAccept && (
                                <div className={s.patientHover}>
                                    <Popover
                                        content={
                                            <div className={s.toolWrap}>
                                                <Spin spinning={fetchingGroupInfo}>
                                                    {tagetData?.patientGroup?.map((group) => {
                                                        return <div key={group}>- {group}</div>;
                                                    })}
                                                </Spin>
                                            </div>
                                        }
                                        trigger="hover"
                                        placement="left"
                                        onOpenChange={(open) => {
                                            if (open && !tagetData) {
                                                doFetchPatientGroupLimitation(item.groupAndLimitationId);
                                            }
                                        }}
                                    >
                                        <span className={s.dotLink}>{item.qtyPatientGroup} groups</span>
                                    </Popover>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>LIMITATION</div>,
                width: '120px',
                render: (val: string[], item: KlarityServiceTypeInfo) => {
                    const hasLimit = !!(item.qtyLimitation && item.qtyLimitation > 0);
                    const key: string = `${item.groupAndLimitationId}`;
                    const tagetData: undefined | TPatientGroupLimitation = groupIdInfoMap.current[key];
                    const isPrescription = item.serviceType === EServiceType.PRESCRIPTION_SERVICE;
                    if (isPrescription) {
                        return <div className={s.colWrap}>-</div>;
                    }
                    return (
                        <div className={s.colWrap}>
                            {!hasLimit && <span>No</span>}
                            {hasLimit && (
                                <div className={s.patientHover}>
                                    <Popover
                                        content={
                                            <div className={s.toolWrap}>
                                                <Spin spinning={fetchingGroupInfo}>
                                                    {tagetData?.limitation?.map((lim) => {
                                                        return <div key={lim}>- {lim}</div>;
                                                    })}
                                                </Spin>
                                            </div>
                                        }
                                        trigger="hover"
                                        placement="left"
                                        onOpenChange={(open) => {
                                            if (open && !tagetData) {
                                                doFetchPatientGroupLimitation(item.groupAndLimitationId);
                                            }
                                        }}
                                    >
                                        <span className={s.dotLink}>{item.qtyLimitation} limitations</span>
                                    </Popover>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: <div className={s.tableTitle}>CONTROLLED MEDS</div>,
                width: '120px',
                render: (
                    val: string[],
                    item: KlarityServiceTypeInfo & { allowControlledMedRefill?: boolean | null },
                ) => {
                    const allow = item.allowControlledMedRefill;
                    return <div className={s.colWrap}>{allow === true ? 'Yes' : 'No'}</div>;
                },
            },
        ];

        return isKiwiUser
            ? serviceTypeColumns.filter((item) => item.key !== 'earning' && item.key !== 'avaPriceInsights')
            : serviceTypeColumns.filter(
                  (item) =>
                      item.key !== 'superBillSupport' &&
                      item.key !== 'priorAuthorizationSupport' &&
                      item.key !== 'insurancesCarrier' &&
                      item.key !== 'insurancesPlan',
              );
    };

    const programColumns: ColumnsType<TKlarityProgramItem> = [
        {
            title: <div className={s.tableTitle}>SPECIALTY</div>,
            dataIndex: 'specialtyName',
            key: 'specialtyName',
            width: '100px',
            render: (val) => {
                return <div className={s.colWrap}>{val}</div>;
            },
        },
        {
            title: <div className={s.tableTitle}>PROGRAM NAME</div>,
            dataIndex: 'programName',
            key: 'programName',
            width: '140px',
            render: (val, row) => {
                return (
                    <div
                        className={`${s.colWrap} ${s.programName}`}
                        onClick={() => {
                            setConfigingProgramId(`${row.id}`);
                            showProgramConfigNote?.();
                        }}
                    >
                        {val}
                    </div>
                );
            },
        },
        {
            title: <div className={s.tableTitle}>STATES</div>,
            dataIndex: 'states',
            key: 'states',
            width: '120px',
            render: (stateCodes: string) => {
                return <div className={s.colWrap}>{stateCodes}</div>;
            },
        },
        {
            title: <div className={s.tableTitle}>DESCRIPTION</div>,
            dataIndex: 'description',
            key: 'description',
            render: (val) => {
                return <div className={s.colWrap}>{val}</div>;
            },
        },
        {
            title: <div className={s.tableTitle}>STATUS</div>,
            dataIndex: 'programStatus',
            key: 'programStatus',
            width: '120px',
            render: (val) => {
                return <div className={s.colWrap}>{val === 'A' ? 'Active' : 'Pending'}</div>;
            },
        },
        {
            title: null,
            width: '120px',
            render: (val: TKlarityProgramItem) => {
                return (
                    <div className={s.colWrap}>
                        <span
                            onClick={() => {
                                setPendingProgram(val);
                                setShowDiscontinueModal(true);
                            }}
                            className={s.btnText}
                        >
                            Discontinue
                        </span>
                    </div>
                );
            },
        },
        {
            title: null,
            width: '120px',
            render: (val: TKlarityProgramItem) => {
                return (
                    <div className={s.colWrap}>
                        <span
                            onClick={() => {
                                setPendingProgram(val);
                                setShowEditStatesModal(true);
                            }}
                            className={s.btnText}
                        >
                            Edit states
                        </span>
                    </div>
                );
            },
        },
    ];
    // if (!isOps) {
    //     programColumns.splice(4, 1);
    // }

    if (!data || data.length === 0) {
        return (
            <div className={s.emptyWrap}>
                {emptyComponent || (
                    <>
                        <img src={emptyImage} alt="empty" />
                        <h3>No services added yet</h3>
                        <p>
                            To start receiving bookings, add at least one service below. You can always edit or update
                            it later.
                        </p>
                        <Button className={s.btn} type="primary" onClick={onClickAdd}>
                            Add new service
                        </Button>
                    </>
                )}
            </div>
        );
    }
    return (
        <div className={`${s.wrap} ${isServiceSetting ? s.serviceSettingWrap : ''}`}>
            {!hideHeader && (
                <div className={`${s.header} ${isServiceSetting ? s.serviceSettingHeader : ''}`}>
                    {isServiceSetting ? (
                        <div className={s.serviceSettingHeaderContent}>
                            <div className={s.headerTitleContent}>
                                <h3 className={s.headerTitle}>Review your service setup</h3>
                                <div className={s.headerDesc}>
                                    Please confirm your service details before moving forward. You’ll be able to make
                                    edits later if needed.
                                </div>
                            </div>
                            <Button className={s.btn} onClick={onClickAdd}>
                                Add new service
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h3>{isKiwiUser ? 'Services' : 'Klarity Service and Price'} </h3>
                            <div className={s.headerRight}>
                                {!isKiwiUser && displaySyncButton && (
                                    <Button
                                        type="primary"
                                        ghost
                                        className={s.btnWithLink}
                                        onClick={onClickSync}
                                        loading={getProfileStore('isSyncingEhr')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                d="M2.66699 2.66797V6.0013H3.05467M13.2924 7.33464C12.9643 4.70376 10.7201 2.66797 8.00033 2.66797C5.76207 2.66797 3.84585 4.04675 3.05467 6.0013M3.05467 6.0013H6.00033M13.3337 13.3346V10.0013H12.946M12.946 10.0013C12.1548 11.9559 10.2386 13.3346 8.00033 13.3346C5.28058 13.3346 3.03632 11.2988 2.70825 8.66797M12.946 10.0013H10.0003"
                                                stroke="#00816B"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        Sync with EHR
                                    </Button>
                                )}
                                {(isOps || showEditButtons) && (
                                    <Button className={s.btn} onClick={onClickAdd}>
                                        {isKiwiUser ? 'Add new service' : 'Add new specialties'}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
            {showTableModule && (
                <div className={`${s.tableWrap} ${isKiwiUser ? s.kiwiTableWrap : ''}`}>
                    {!isKiwiUser && hasEnrollProgram && (
                        <>
                            <div className={s.tableHeader}>
                                <span>Klarity select programs</span>
                            </div>
                            <div className={s.programTableWrap}>
                                <CommonTable
                                    bordered
                                    rowKey="rowId"
                                    columns={programColumns}
                                    data={enrollPrograms || []}
                                    pagination={false}
                                    scroll={{ x: true }}
                                />
                            </div>
                        </>
                    )}
                    {!isKiwiUser && hasUnEnrollPrograms && !isOps && (
                        <div className={s.pendingActive}>
                            <div className={s.label}>
                                You haven’t yet enrolled in any Klarity Select programs tailored to your specialties.
                                Here is a curated list of targeted programs designed to help you attract more patients
                                and grow your practice. Explore these options to enhance your service offerings.
                            </div>
                            <div className={s.items}>
                                <ul>
                                    {unEnrollPrograms?.map((item) => {
                                        return (
                                            <li
                                                onClick={() => {
                                                    onClickConfigProgram && onClickConfigProgram(item);
                                                }}
                                                key={item.programName}
                                            >
                                                <span>{item.programName}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {!loading && (!data || data.length === 0) && (
                <div className={s.empty}>{`You haven't set up any ${isKiwiUser ? 'Kiwi' : 'Klarity'} service`}</div>
            )}
            <Spin spinning={loading || fetchSpecialtiesConditionsLoading}>
                <div className={s.content}>
                    {data?.map((service: TKlarityServiceUiTableItem) => {
                        const tableData = [...(service?.serviceTypeInfo || [])];
                        tableData?.sort((a, b) => {
                            return a.state && b.state ? a.state.dictKey.localeCompare(b.state.dictKey) : 0;
                        });
                        const specialtyId = service.serviceSpecialtyId;
                        const conditions =
                            specialtiesConditions.find((item) => item.specialtyId === specialtyId)?.conditions || '';
                        const conditionsArray = conditions?.split(',') || [];
                        const maxLength = 6;
                        const safeIndex = Math.min(maxLength, conditionsArray.length);
                        const displayCondition = conditionsArray?.slice(0, safeIndex) || [];
                        const moreCondition = conditionsArray?.slice(safeIndex) || [];
                        const hasMoreCondition = moreCondition.length > 0;

                        return (
                            <div className={s.tableWrap} key={service.serviceSpecialtyId}>
                                <div className={s.tableHeader}>
                                    <div className={s.headerTop}>
                                        <span>{service.serviceSpecialtyName}</span>
                                        <div className={s.topRight}>
                                            {isOps || showEditButtons ? (
                                                <Button
                                                    onClick={() => {
                                                        onClickEdit(service);
                                                    }}
                                                    size="small"
                                                    className={s.btn}
                                                >
                                                    Edit
                                                </Button>
                                            ) : (
                                                <span />
                                            )}
                                            {canRemoveSpe && (
                                                <Button
                                                    onClick={() => {
                                                        onClickRemove(service);
                                                    }}
                                                    size="small"
                                                    className={s.btn}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.conditionWrap}>
                                        {displayCondition?.map((condi: string) => {
                                            return (
                                                <div className={s.condi} key={condi}>
                                                    {condi}
                                                </div>
                                            );
                                        })}
                                        {hasMoreCondition && (
                                            <Popover
                                                content={<div className={s.toolWrap}>{moreCondition?.join(', ')}</div>}
                                                trigger="hover"
                                            >
                                                <div className={s.more}>{moreCondition.length} more</div>
                                            </Popover>
                                        )}
                                    </div>
                                </div>
                                <div className={s.tableContent}>
                                    <CommonTable
                                        bordered
                                        rowKey="rowId"
                                        columns={getColumnsByData(tableData, service.serviceSpecialtyName)}
                                        data={tableData || []}
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Spin>
            <Modal
                open={showDiscontinueModal}
                okText="Discontinue"
                onCancel={() => {
                    setShowDiscontinueModal(false);
                }}
                okButtonProps={{
                    danger: true,
                    loading: discontinueProgramLoading,
                    style: {
                        display: programIsActiveInPPP ? 'none' : 'inline-block',
                    },
                }}
                closable={false}
                onOk={handleDiscontinue}
                cancelText={programColumns ? 'Close' : 'Cancel'}
            >
                <div className={s.mModal}>
                    <div className={s.mContent}>
                        <div className={s.icon}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M12.0015 9V11M12.0015 15H12.0115M5.0733 19H18.9297C20.4693 19 21.4316 17.3333 20.6618 16L13.7336 4C12.9637 2.66667 11.0392 2.66667 10.2694 4L3.34125 16C2.57145 17.3333 3.5337 19 5.0733 19Z"
                                    stroke="#DC2626"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className={s.info}>
                            <div className={s.mTitle}>
                                {programIsActiveInPPP ? 'Contact support to discontinue' : 'Confirm discontinuation'}
                            </div>
                            {programIsActiveInPPP ? (
                                <div className={s.mDesc}>
                                    To discontinue this program while your profile is active, please contact Provider
                                    Support (
                                    <a href={`mailto:${Email.klarity_provider_support}`}>
                                        {Email.klarity_provider_support}
                                    </a>
                                    ) for assistance. This ensures a smooth transition and handling of ongoing patient
                                    engagements.
                                </div>
                            ) : (
                                <div className={s.mDesc}>
                                    Are you sure you want to discontinue {pendingProgram?.programName}? By
                                    discontinuing, this program will not be added to your profile, and you won’t be able
                                    to use it to attract new patients at this time. You can always choose to add this
                                    program again later.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                open={showEditStatesModal}
                title="Applied states"
                okText="Save"
                onCancel={() => {
                    setShowEditStatesModal(false);
                }}
                closable
                okButtonProps={{
                    loading: updateProgramStateLoading,
                }}
                onOk={handleEditStates}
            >
                <div className={s.mModal}>
                    <div className={s.info}>
                        <div className={s.mTitle}>Edit states in the {pendingProgram?.programName}</div>
                        <div className={s.mDesc}>
                            This program is not available in states: {notSupportStateDicKeys?.join(', ')}.
                        </div>
                        <Select
                            mode="multiple"
                            value={pendingProgramStates?.map((st) => st.dictKey)}
                            style={{ width: '360px', marginTop: '12px' }}
                            onChange={(stateDisKeyss: string[]) => {
                                const _states = licenseStates?.filter((st) => {
                                    return stateDisKeyss?.includes(st.content) || stateDisKeyss?.includes(st.dictKey);
                                });
                                setPendingProgramStates(_states || []);
                            }}
                            maxTagCount="responsive"
                            // tagRender={(pop) => {
                            //     const target = allowStates?.find((item) => item.dictKey === pop.value);
                            //     return <span className={s.displayLabel}>{target ? target.content : pop.value}</span>;
                            // }}
                        >
                            {supportStates?.map((ste) => {
                                return (
                                    <Select.Option
                                        disabled={
                                            pendingProgramStates &&
                                            pendingProgramStates.length === 1 &&
                                            pendingProgramStates[0].dictKey === ste.dictKey
                                        }
                                        key={ste.dictKey}
                                        value={ste.dictKey}
                                    >
                                        {ste.content}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>
            </Modal>

            <Modal
                open={showRemoveSpeConfirmModal}
                title={`Remove ${pendingRemoveSpe?.serviceSpecialtyName}?`}
                onCancel={() => {
                    setShowRemoveSpeConfirmModal(false);
                    setPendingRemoveSpe(undefined);
                }}
                onOk={handleConfirmRemoveSpe}
                okText="Confirm"
                okButtonProps={{
                    loading: removeSpeLoading,
                }}
            >
                <div className={s.mModal}>
                    <div className={s.mContent}>
                        Do you want to remove all services from {pendingRemoveSpe?.serviceSpecialtyName}?
                    </div>
                </div>
            </Modal>

            {isServiceSetting && !hideFooter && (
                <div className={s.footer}>
                    <div className={s.left}>
                        <Button
                            onClick={() => {
                                navigate(`${PATH.DASHBOARD}`);
                            }}
                        >
                            Exit
                        </Button>
                        {/* {
                            !settingIsDone && (
                                <Button type="link" className={s.backLink} onClick={onBack}>
                                    Back
                                </Button>
                            )
                        } */}
                    </div>
                    <div className={s.right}>
                        <Button
                            type="primary"
                            disabled={!data || data.length === 0}
                            onClick={() => {
                                navigate(`/${PATH.SERVICE_SETTING_SUCCESS}`);
                            }}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceTable;
